import type { SalaryChangeRequest, StaffProfile } from "../services/staffService";

export type PayChangeFilters = {
  search: string;
  status: string;
  /** A department id, "none" for school-wide staff, or "" for everyone. */
  department: string;
  from: string;
  to: string;
};

export const EMPTY_PAY_CHANGE_FILTERS: PayChangeFilters = {
  search: "",
  status: "",
  department: "",
  from: "",
  to: "",
};

export const PAY_CHANGE_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  submitted: "Awaiting approval",
  returned: "Returned",
  verified: "Verified",
  approved: "Approved",
  posted: "Posted",
  rejected: "Rejected",
  reversed: "Reversed",
  cancelled: "Cancelled",
};

export const PAY_CHANGE_STATUS_DOTS: Record<string, string> = {
  submitted: "bg-amber-500",
  returned: "bg-orange-500",
  verified: "bg-blue-500",
  approved: "bg-green-500",
  posted: "bg-green-700",
  rejected: "bg-red-500",
};

export const PAY_CHANGE_STATUS_TABS: [string, string][] = [
  ["", "All"],
  ["submitted", "Awaiting approval"],
  ["returned", "Returned"],
  ["approved", "Approved"],
  ["rejected", "Rejected"],
];

const allowanceTotal = (lines?: { amount: string }[]) =>
  (lines ?? []).reduce((sum, line) => sum + (Number(line.amount) || 0), 0);

/** Gross pay before and after the change. */
export function grossChange(change: SalaryChangeRequest) {
  const before = Number(change.previous_salary || 0) + allowanceTotal(change.previous_allowances);
  const after = Number(change.proposed_salary || 0) + allowanceTotal(change.proposed_allowances);
  return { before, after, difference: after - before };
}

export function activePayChangeFilterCount(filters: PayChangeFilters) {
  return Object.values(filters).filter((value) => value.trim()).length;
}

/** How many requests sit in each status, for the counts on the tabs. */
export function payChangeStatusCounts(changes: SalaryChangeRequest[]) {
  const counts: Record<string, number> = { "": changes.length };
  for (const change of changes) {
    counts[change.workflow_status] = (counts[change.workflow_status] ?? 0) + 1;
  }
  return counts;
}

/**
 * Requests matching the filters, the ones awaiting a decision first and then
 * the latest effective date.
 */
export function filterPayChanges(
  changes: SalaryChangeRequest[],
  filters: PayChangeFilters,
  profiles: StaffProfile[] = [],
  { ignoreStatus = false } = {},
) {
  const departmentOf = new Map(profiles.map((profile) => [profile.id, profile.department]));
  const term = filters.search.trim().toLowerCase();
  return changes
    .filter((change) => {
      const department = departmentOf.get(change.staff);
      return (
        (!term ||
          [change.staff_name, change.reason, change.initiated_by_name, change.approved_by_name ?? ""]
            .some((value) => value.toLowerCase().includes(term))) &&
        (ignoreStatus || !filters.status || change.workflow_status === filters.status) &&
        (!filters.department ||
          (filters.department === "none"
            ? department == null
            : String(department ?? "") === filters.department)) &&
        (!filters.from || change.effective_date >= filters.from) &&
        (!filters.to || change.effective_date <= filters.to)
      );
    })
    .sort((a, b) => {
      const waiting = Number(b.workflow_status === "submitted") - Number(a.workflow_status === "submitted");
      return waiting || b.effective_date.localeCompare(a.effective_date) || b.id - a.id;
    });
}
